import {
  ArrowRight,
  CalendarClock,
  Database,
  FileSignature,
  Inbox,
  ListTodo,
  Megaphone,
  MessageCircle,
  Newspaper,
  Search,
  type LucideIcon,
} from 'lucide-react';

import markUrl from '@/assets/sancus-mark.svg';
import avatar1 from '@/assets/landing/avatar-1.webp';
import avatar2 from '@/assets/landing/avatar-2.webp';
import avatar3 from '@/assets/landing/avatar-3.webp';
import avatar4 from '@/assets/landing/avatar-4.webp';

import { FrameBadge, type BadgeTone } from './app-frame';

/*
 * Pedaços das telas do CRM para os cards de recurso, com os mesmos cartões,
 * selos e textos do produto. Ficam ancorados embaixo e à direita e são
 * cortados pelo card, como um recorte de tela. Dados fictícios e decorativos:
 * quem descreve o recurso é o texto do card.
 */

const card = 'border-outline-variant bg-surface-container-lowest text-on-surface border';

export function DayFragment() {
  const summary: Array<[string, string, LucideIcon, string]> = [
    ['3', 'Prazos de hoje', CalendarClock, 'bg-error-container text-error'],
    ['12', 'Tarefas pendentes', ListTodo, 'bg-tertiary-fixed text-on-tertiary-fixed-variant'],
    ['5', 'Intimações novas', Inbox, 'bg-secondary-fixed text-on-secondary-fixed'],
  ];

  return (
    <div className={`${card} ml-6 w-[600px] rounded-tl-xl border-b-0 border-r-0 p-5`}>
      <p className="text-on-surface-variant text-micro mb-1 uppercase">Operação diária</p>
      <p className="text-headline-md">Painel de hoje</p>
      <div className="mt-4 grid grid-cols-3 gap-3">
        {summary.map(([value, label, Icon, iconClass]) => (
          <div key={label} className={`${card} flex items-center gap-3 rounded-xl p-3`}>
            <span
              className={`flex size-9 shrink-0 items-center justify-center rounded-xl ${iconClass}`}
            >
              <Icon className="size-4.5" aria-hidden="true" />
            </span>
            <span>
              <span className="block text-xl font-semibold tabular-nums leading-tight">
                {value}
              </span>
              <span className="block text-[12px] font-medium">{label}</span>
            </span>
          </div>
        ))}
      </div>
      <div className={`${card} mt-3 overflow-hidden rounded-xl`}>
        <div className="border-outline-variant flex items-center justify-between border-b px-4 py-3">
          <span className="text-body-sm flex items-center gap-2 font-semibold">
            <CalendarClock className="text-error size-4" aria-hidden="true" /> Prazos de hoje
          </span>
          <FrameBadge tone="error">3</FrameBadge>
        </div>
        {[
          ['Contestação', 'Costa × Construtora Alfa · Marina Costa'],
          ['Réplica', 'Almeida × Banco Meridiano · João Almeida'],
        ].map(([title, meta]) => (
          <div
            key={title}
            className="border-outline-variant flex items-center gap-3 border-b px-4 py-3 last:border-b-0"
          >
            <span className="min-w-0 flex-1">
              <span className="block text-[13px] font-medium">{title}</span>
              <span className="text-on-surface-variant block truncate text-xs">{meta}</span>
            </span>
            <ArrowRight className="text-outline size-4" aria-hidden="true" />
          </div>
        ))}
      </div>
    </div>
  );
}

export function PublicationsFragment() {
  const rows: Array<[string, string, string, BadgeTone, string]> = [
    ['TJSP', 'Intimação', 'Costa × Construtora Alfa', 'success', 'Vinculada'],
    ['TRT-2', 'Designação de audiência', 'Lima × Transportes Rota', 'success', 'Vinculada'],
    ['TRF-3', 'Sentença', 'Vértice × Fazenda Nacional', 'success', 'Vinculada'],
    ['TJMG', 'Despacho', 'Sem processo vinculado', 'warning', 'Triagem'],
  ];

  return (
    <div className="w-full max-w-[440px] space-y-2.5 px-6">
      {rows.map(([court, title, caseTitle, tone, status], index) => (
        <div
          key={title}
          className={`${card} flex items-center gap-3 rounded-xl px-4 py-3 shadow-[var(--elevation-raised)]`}
          style={{ marginLeft: index % 2 ? 18 : 0, marginRight: index % 2 ? 0 : 18 }}
        >
          <span className="min-w-0 flex-1">
            <span className="flex items-center gap-2">
              <FrameBadge tone="info">{court}</FrameBadge>
              <span className="truncate text-[13px] font-medium">{title}</span>
            </span>
            <span className="text-on-surface-variant mt-1 block truncate text-xs">{caseTitle}</span>
          </span>
          <FrameBadge tone={tone}>{status}</FrameBadge>
        </div>
      ))}
    </div>
  );
}

export function ClientsFragment() {
  const rows: Array<[string, string, string, string]> = [
    [avatar1, 'Marina Costa', 'Contrato de honorários assinado', 'hoje'],
    [avatar2, 'João Almeida', 'Audiência designada para 14/10', 'ontem'],
    [avatar3, 'Fernanda Lima', 'Documentos enviados pelo portal', '28/09'],
    [avatar4, 'Carlos Nunes', 'Recurso protocolado', '25/09'],
  ];

  return (
    <div className={`${card} ml-6 w-[360px] rounded-tl-xl border-b-0 border-r-0 p-4`}>
      <span className="text-body-sm flex items-center gap-2 font-semibold">
        <span className="bg-primary size-2 rounded-full" /> Timeline dos clientes
      </span>
      <ul className="divide-outline-variant mt-3 divide-y">
        {rows.map(([src, name, event, when]) => (
          <li key={name} className="flex items-center gap-3 py-2.5">
            <img
              src={src}
              alt=""
              width="34"
              height="34"
              loading="lazy"
              className="size-[34px] rounded-full object-cover"
            />
            <span className="min-w-0 flex-1">
              <strong className="block text-[13px] font-medium">{name}</strong>
              <span className="text-on-surface-variant block truncate text-xs">{event}</span>
            </span>
            <span className="text-outline text-[11px]">{when}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function InvoicesFragment() {
  const invoices: Array<[string, string, string, BadgeTone, string]> = [
    ['FAT-2026-0142', 'Marina Costa', 'R$ 4.800,00', 'success', 'Paga'],
    ['FAT-2026-0141', 'Grupo Vértice', 'R$ 12.000,00', 'warning', 'Parcialmente paga'],
    ['FAT-2026-0140', 'João Almeida', 'R$ 2.500,00', 'info', 'Enviada'],
  ];

  return (
    <div className="w-full max-w-[340px] px-6">
      <div className="grid grid-cols-2 gap-2.5">
        {[
          ['Recebido no mês', 'R$ 52.100'],
          ['Em aberto', 'R$ 38.400'],
        ].map(([label, value]) => (
          <div key={label} className={`${card} rounded-xl p-3`}>
            <p className="text-on-surface-variant text-[10px] font-semibold uppercase tracking-wider">
              {label}
            </p>
            <p className="mt-1 text-[17px] font-semibold">{value}</p>
          </div>
        ))}
      </div>
      <ul
        className={`${card} divide-outline-variant mt-2.5 divide-y rounded-xl shadow-[var(--elevation-raised)]`}
      >
        {invoices.map(([number, client, total, tone, status]) => (
          <li key={number} className="flex items-center gap-2 px-3 py-2.5">
            <span className="min-w-0 flex-1">
              <span className="block text-[12px] font-semibold">{number}</span>
              <span className="text-on-surface-variant block truncate text-[11px]">
                {client} · {total}
              </span>
            </span>
            <FrameBadge tone={tone}>{status}</FrameBadge>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function OrbitFragment() {
  // Posições calculadas sobre os anéis (raios de 120 e 85 px num quadro de 300 × 250).
  const nodes: Array<{ icon: LucideIcon; x: number; y: number }> = [
    { icon: Newspaper, x: 13.7, y: 29.7 },
    { icon: Megaphone, x: 86.3, y: 29.7 },
    { icon: Search, x: 86.3, y: 70.3 },
    { icon: MessageCircle, x: 13.7, y: 70.3 },
    { icon: Database, x: 50, y: 16 },
    { icon: FileSignature, x: 50, y: 84 },
  ];

  return (
    <div className="relative mx-auto h-[250px] w-[300px]">
      {[240, 170, 100].map((size) => (
        <span
          key={size}
          className="border-primary/25 absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-dashed"
          style={{ width: size, height: size }}
        />
      ))}
      <span className="absolute left-1/2 top-1/2 grid size-16 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full bg-white shadow-[0_0_0_10px_rgb(12_80_227/0.08),0_14px_30px_-12px_rgb(12_80_227/0.5)]">
        <img src={markUrl} alt="" width="30" height="30" className="size-7" />
      </span>
      {nodes.map(({ icon: Icon, x, y }, index) => (
        <span
          key={index}
          className={`${card} text-primary absolute grid size-11 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-xl shadow-[var(--elevation-raised)]`}
          style={{ left: `${x}%`, top: `${y}%` }}
        >
          <Icon size={18} />
        </span>
      ))}
    </div>
  );
}
